import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  // PR-2 (Feb 2026) — last time the feature-flag localStorage blob was refreshed.
  // Drives the "Flags as of HH:MM" timestamp on the Admin → Feature Flags page.
  // null = never refreshed in this session.
  const [featureFlagsRefreshedAt, setFeatureFlagsRefreshedAt] = useState(null);

  const fetchUser = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setLoading(false);
        return;
      }
      const res = await api.get('/auth/me');
      const userData = res.data.user;
      setUser(userData);
      try { localStorage.setItem('gc360_permissions', JSON.stringify(userData?.permissions || [])); } catch (e) {}
      // Sync feature flags for notification engine
      try {
        const fr = await api.get('/production/feature-flags/mine');
        localStorage.setItem('gc360_feature_flags', JSON.stringify(fr.data?.data || {}));
        setFeatureFlagsRefreshedAt(new Date().toISOString());
      } catch { /* non-fatal */ }
      // Check if must_change_password flag is set
      if (userData?.must_change_password) {
        setMustChangePassword(true);
      }
    } catch (e) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync feature flags to localStorage for notification engine.
  // PR-2 (Feb 2026): exposed via AuthContext so admins can refresh without
  // a full page reload. Returns the ISO timestamp on success, or null on failure.
  const syncFeatureFlags = useCallback(async () => {
    try {
      const r = await api.get('/production/feature-flags/mine');
      const flags = r.data?.data || {};
      localStorage.setItem('gc360_feature_flags', JSON.stringify(flags));
      const ts = new Date().toISOString();
      setFeatureFlagsRefreshedAt(ts);
      return ts;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password, rememberMe = false) => {
    const res = await api.post('/auth/login', { email, password, remember_me: rememberMe });
    // Phase 4B-3b — two-stage login: no tokens are issued yet; the restricted
    // challenge token is kept in React state only (never localStorage).
    if (res.data?.two_factor_required) {
      return {
        twoFactorRequired: true,
        challengeToken: res.data.challenge_token,
        expiresInSeconds: res.data.expires_in_seconds,
        methods: res.data.methods || ['totp', 'recovery_code'],
      };
    }
    const { access_token, refresh_token, user: userData, must_change_password: mcp } = res.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    setUser(userData);
    try { localStorage.setItem('gc360_permissions', JSON.stringify(userData?.permissions || [])); } catch (e) {}
    if (mcp) {
      setMustChangePassword(true);
    }
    syncFeatureFlags();
    return userData;
  };

  // Completes a 2FA login challenge (TOTP or recovery code). Only after this
  // succeeds are normal access/refresh tokens stored.
  const completeTwoFactor = async (challengeToken, method, code) => {
    const res = await api.post('/auth/2fa/challenge/verify', {
      challenge_token: challengeToken, method, code,
    });
    const { access_token, refresh_token, user: userData, must_change_password: mcp } = res.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    setUser(userData);
    try { localStorage.setItem('gc360_permissions', JSON.stringify(userData?.permissions || [])); } catch (e) {}
    if (mcp) setMustChangePassword(true);
    syncFeatureFlags();
    return userData;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      // Ignore
    }
    // Day-8: clear sensitive offline caches (preserves unsynced queue by default)
    try {
      const { clearSensitiveOfflineData } = await import('./offline/storage');
      await clearSensitiveOfflineData();
    } catch (e) {
      // Offline storage may be unavailable; ignore
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    try { localStorage.removeItem('gc360_permissions'); } catch (e) {}
    // Staff Portal v2 — invalidate the per-user feature-flag cache so that a
    // subsequent login (same tab) does not inherit the previous user's flags.
    try {
      const { clearStaffV2FlagCache } = await import('./staffV2Flags');
      clearStaffV2FlagCache();
    } catch (e) { /* module optional */ }
    try {
      const { clearStaffV2NotificationState } = await import('../contexts/StaffNotificationsContext');
      clearStaffV2NotificationState();
    } catch (e) { /* module optional */ }
    setUser(null);
    setMustChangePassword(false);
  };

  const onPasswordChanged = async () => {
    setMustChangePassword(false);
    // Re-fetch user to get updated data
    await fetchUser();
  };

  // Default role templates resolve by name (fast path). Day 14: organisation custom roles resolve from their
  // permissions — a custom role with back-office capabilities gets the admin shell instead of a blank dashboard.
  // Day 15 fix: ANY custom role that is not a worker-type role (e.g. "Audit Manager") gets the admin shell; the
  // sidebar is already permission-filtered, so a read-only role simply sees its permitted modules.
  const ADMIN_ROLES = ['Super Admin', 'Admin', 'Manager', 'HR', 'Finance', 'Coordinator', 'Roster Manager'];   // RBAC-2: Manager = Admin's peer
  const STAFF_ROLES = ['Support Worker', 'Staff'];
  const ADMIN_CAPABILITIES = ['org.manage_roles', 'auth.manage_users', 'participants.manage', 'roster.manage', 'invoices.manage', 'funding.manage', 'staff.hr.view', 'timesheets.approve'];
  const WORKER_CAPABILITIES = ['roster.open_shift_pickup', 'roster.request_swap', 'matching.feedback.respond'];
  const hasPermission = (perm) => user?.permissions?.includes(perm) || false;
  const isWorkerRole = STAFF_ROLES.includes(user?.role_name) || (!!user && !ADMIN_ROLES.includes(user?.role_name) && WORKER_CAPABILITIES.some(hasPermission) && !ADMIN_CAPABILITIES.some(hasPermission));
  const isAdmin = !!user && !isWorkerRole;
  const isStaff = !!user && isWorkerRole;

  return (
    <AuthContext.Provider value={{
      user, loading, login, completeTwoFactor, logout, isAdmin, isStaff, hasPermission, fetchUser,
      mustChangePassword, onPasswordChanged,
      syncFeatureFlags, featureFlagsRefreshedAt,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
